import type { MatrixClient } from 'matrix-js-sdk';
import type { CryptoCallbacks } from 'matrix-js-sdk/lib/crypto-api/index.js';
import { decodeRecoveryKey } from 'matrix-js-sdk/lib/crypto-api/recovery-key.js';
import { useSyncExternalStore } from 'react';
import { friendlyError } from '../friendlyError.js';

/**
 * Recuperação das conversas (T2.2.2). A chave de recuperação protege o
 * armazenamento secreto do servidor, onde ficam o backup das chaves das salas
 * e as chaves de assinatura cruzada. Primeiro aparelho: cria e mostra a chave
 * uma vez. Aparelho novo: pede a chave para destravar o histórico.
 */
export type RecoveryPhase =
  'idle' | 'checking' | 'setup' | 'show-key' | 'unlock' | 'ready' | 'error';

export type RecoveryState = {
  phase: RecoveryPhase;
  generatedKey: string | null;
  error: string;
  busy: boolean;
  /** Aparelho novo que ainda não destravou o histórico (a pessoa escolheu "Depois"). */
  needsUnlock: boolean;
};

const IDLE: RecoveryState = {
  phase: 'idle',
  generatedKey: null,
  error: '',
  busy: false,
  needsUnlock: false,
};
let state: RecoveryState = IDLE;
/** "Depois" vale por alguns dias neste aparelho; o histórico pode ser destravado em Aparelhos. */
const LATER_KEY = 'secretario.chave-depois';
const LATER_MS = 3 * 24 * 60 * 60 * 1000;

function postponedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(LATER_KEY) ?? 0);
    return Date.now() - at < LATER_MS;
  } catch {
    return false;
  }
}
let pendingKey: Uint8Array<ArrayBuffer> | null = null;
let pendingKeyId: string | null = null;
const listeners = new Set<() => void>();

function set(patch: Partial<RecoveryState>) {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

export const recoveryCallbacks: CryptoCallbacks = {
  getSecretStorageKey: async ({ keys }) => {
    if (!pendingKey) return null;
    // Só entrega a chave para o id que ela abre; nunca tenta uma chave na porta errada.
    const keyId = pendingKeyId && keys[pendingKeyId] ? pendingKeyId : Object.keys(keys)[0];
    return keyId && (!pendingKeyId || keyId === pendingKeyId) ? [keyId, pendingKey] : null;
  },
  cacheSecretStorageKey: (keyId, _info, key) => {
    pendingKey = key;
    pendingKeyId = keyId;
  },
};

// Primeiro envio das chaves de assinatura não exige reautenticação (MSC3967).
const uploadSigningKeys = async (makeRequest: (auth: null) => Promise<unknown>) => {
  await makeRequest(null);
};

export async function evaluateRecovery(client: MatrixClient): Promise<void> {
  const crypto = client.getCrypto();
  if (!crypto) {
    set({ phase: 'ready' });
    return;
  }
  set({ phase: 'checking', error: '' });
  try {
    const backup = await crypto.getKeyBackupInfo();
    const defaultKeyId = await client.secretStorage.getDefaultKeyId();
    if (!backup && !defaultKeyId) {
      set({ phase: 'setup' });
      return;
    }
    const cached = await crypto.getSessionBackupPrivateKey();
    if (cached) {
      await crypto.checkKeyBackupAndEnable();
      set({ phase: 'ready', needsUnlock: false });
      return;
    }
    set(postponedRecently() ? { phase: 'ready', needsUnlock: true } : { phase: 'unlock' });
  } catch (error) {
    set({
      phase: 'error',
      error: friendlyError(error, 'Não consegui conferir a proteção.'),
    });
  }
}

export async function setupRecovery(client: MatrixClient): Promise<void> {
  const crypto = client.getCrypto();
  if (!crypto || state.busy) return;
  set({ busy: true, error: '' });
  try {
    let generated: string | null = null;
    await crypto.bootstrapCrossSigning({ authUploadDeviceSigningKeys: uploadSigningKeys });
    await crypto.bootstrapSecretStorage({
      createSecretStorageKey: async () => {
        const key = await crypto.createRecoveryKeyFromPassphrase();
        generated = key.encodedPrivateKey ?? null;
        pendingKey = key.privateKey;
        return key;
      },
      setupNewKeyBackup: true,
    });
    await crypto.checkKeyBackupAndEnable();
    set({ phase: 'show-key', generatedKey: generated, busy: false });
  } catch (error) {
    set({
      busy: false,
      phase: 'setup',
      error: friendlyError(error, 'Não consegui criar a proteção agora.'),
    });
  }
}

/**
 * Troca a chave de recuperação (quem perdeu a antiga). O armazenamento secreto
 * é recriado com uma chave nova; o backup das salas continua se este aparelho
 * já tinha a chave dele em cache, senão nasce um backup novo daqui em diante.
 */
export async function regenerateRecovery(client: MatrixClient): Promise<void> {
  const crypto = client.getCrypto();
  if (!crypto || state.busy) return;
  set({ busy: true, error: '', phase: 'setup' });
  try {
    let generated: string | null = null;
    const hasBackupKey = !!(await crypto.getSessionBackupPrivateKey());
    // Com a assinatura cruzada já pronta, o bootstrap dela leria o armazenamento
    // antigo (chave perdida): só criamos quando não existe.
    if (!(await crypto.isCrossSigningReady())) {
      await crypto.bootstrapCrossSigning({ authUploadDeviceSigningKeys: uploadSigningKeys });
    }
    await crypto.bootstrapSecretStorage({
      setupNewSecretStorage: true,
      createSecretStorageKey: async () => {
        const key = await crypto.createRecoveryKeyFromPassphrase();
        generated = key.encodedPrivateKey ?? null;
        pendingKey = key.privateKey;
        return key;
      },
      setupNewKeyBackup: !hasBackupKey,
    });
    await crypto.checkKeyBackupAndEnable();
    set({ phase: 'show-key', generatedKey: generated, busy: false });
  } catch (error) {
    set({
      busy: false,
      phase: 'ready',
      error: friendlyError(error, 'Não consegui trocar a chave agora.'),
    });
  }
}

export async function unlockWithKey(client: MatrixClient, keyText: string): Promise<boolean> {
  const crypto = client.getCrypto();
  if (!crypto || state.busy) return false;
  set({ busy: true, error: '' });
  try {
    pendingKey = decodeRecoveryKey(keyText.replace(/\s+/g, ' ').trim());
    pendingKeyId = await client.secretStorage.getDefaultKeyId();
    await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
    await crypto.checkKeyBackupAndEnable();
    await crypto.restoreKeyBackup();
    await crypto.bootstrapCrossSigning({ authUploadDeviceSigningKeys: uploadSigningKeys });
    try {
      localStorage.removeItem(LATER_KEY);
    } catch {
      /* nada a limpar */
    }
    set({ phase: 'ready', busy: false, needsUnlock: false });
    return true;
  } catch {
    pendingKey = null;
    pendingKeyId = null;
    set({ busy: false, error: 'Essa chave não bate. Confira os caracteres e tente de novo.' });
    return false;
  }
}

export function confirmKeySaved(): void {
  set({ phase: 'ready', generatedKey: null });
}

export function postponeRecovery(): void {
  try {
    localStorage.setItem(LATER_KEY, String(Date.now()));
  } catch {
    /* sem armazenamento: só esta sessão fica sem a pergunta */
  }
  set({ phase: 'ready', needsUnlock: true });
}

/** Reabre o pedido da chave (a pessoa quer destravar o histórico agora). */
export function askRecoveryKey(): void {
  set({ phase: 'unlock', error: '' });
}

export function resetRecovery(): void {
  pendingKey = null;
  pendingKeyId = null;
  state = IDLE;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useRecoveryState(): RecoveryState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}
